import * as Icon from 'base/ui/components/Icons';

import { Form, Modal, Tooltip, message } from 'antd';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ExclamationCircleFilled } from '@ant-design/icons';
import { ProColumns } from '@ant-design/pro-components';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomInput from 'base/ui/components/CustomInput';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { usePermission } from 'hooks/usePermission';
import { observer } from 'mobx-react';
import { HiSearch } from 'react-icons/hi';
import { dropdownStatus } from 'utils/dropdown';
import { useTranslation } from 'react-i18next';
import { customFilterOption } from 'utils/searchHelper';
import { getRoles } from 'utils/localStorageHelper';
import {
  userStore,
  wasteGroupingEnvironmentPermitPresenter,
  wasteGroupingEnvironmentPermitStore,
  commonMasterDataPresenter,
  commonMasterDataStore,
} from 'stores/root_store';
import {
  WasteGroupingEnvironmentPermitModel,
  WasteGroupingEnvironmentPermitSearchPayload,
} from 'services/RawData/wasteGroupingEnvironmentPermit_services';
import { PAGE_SIZE_DEFAULT } from 'utils/constants';
import { formatDecimalNumber } from 'utils/formatHelper';

const { confirm } = Modal;

const createWasteGroupingEnvironmentPermitPage = () => {
  return observer(() => {
    usePermission('INDEX');
    const { t } = useTranslation();
    const [form] = Form.useForm<any>();
    const navigate = useNavigate();
    const [openDrawer, setOpenDrawer] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayData, setDisplayData] = useState<WasteGroupingEnvironmentPermitModel[]>([]);
    const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string }[]>([]);
    const [dataSearch, setDataSearch] = useState<WasteGroupingEnvironmentPermitSearchPayload>({
      actived: true,
      paging: {
        pageIndex: 1,
        pageSize: PAGE_SIZE_DEFAULT,
        orderBy: '',
        orderByDesc: '',
      },
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [importLoading, setImportLoading] = useState(false);

    // Check role SYSADMIN từ localStorage
    const roles = getRoles() || [];
    const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',').map(r => r.trim()) : []);
    const isSysAdmin = rolesArray.includes('SYSADMIN');

    // const dataWithIndex = useMemo(
    //   () =>
    //     displayData.map((item, index) => ({
    //       ...item,
    //       // stt: index + 1,
    //     })),
    //   [displayData],
    // );

    const loadWasteGroups = async () => {
      await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'WasteType');
      if (commonMasterDataStore.commonRespone.isSuccess && commonMasterDataStore.commonRespone.data) {
        const options = commonMasterDataStore.commonRespone.data.map((item) => ({
          label: item.value,
          value: item.key,
        }));
        setWasteGroupOptions(options);
      }
    };

    const loadData = async (payload: WasteGroupingEnvironmentPermitSearchPayload) => {
      setLoading(true);
      await wasteGroupingEnvironmentPermitPresenter.searchWasteGroupingEnvironmentPermits(
        wasteGroupingEnvironmentPermitStore,
        payload
      );
      if (wasteGroupingEnvironmentPermitStore.searchResponse.isSuccess) {
        const data = wasteGroupingEnvironmentPermitStore.searchResponse.data || [];
        setDisplayData(data);
      } else {
        message.error(wasteGroupingEnvironmentPermitStore.searchResponse.message);
      }
      setLoading(false);
    };

    useEffect(() => {
      loadWasteGroups();
      // Giá trị mặc định đã được set trong initialValues của SearchForm
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      loadData(dataSearch);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSearch]);

    const handleSearch = (values: any) => {
      // Xử lý group: lấy giá trị wasteGroup từ catalog WasteType
      let wasteGroup: string | undefined;

      if (values.group) {
        if (Array.isArray(values.group)) {
          // Nếu là mảng, lấy giá trị đầu tiên (hoặc có thể xử lý khác tùy yêu cầu)
          wasteGroup = values.group[0];
        } else {
          wasteGroup = values.group;
        }
      }

      const payload: WasteGroupingEnvironmentPermitSearchPayload = {
        wasteGroupingEnvironmentPermitCustomCode: values.code || undefined,
        wasteGroupingEnvironmentPermitName: values.name || undefined,
        wasteGroup,
        actived: typeof values.actived === 'boolean' ? values.actived : undefined,
        paging: {
          ...dataSearch.paging,
          pageIndex: 1,
        },
      };
      setDataSearch(payload);
      setOpenDrawer(false);
    };

    const columns: ProColumns<WasteGroupingEnvironmentPermitModel & { stt: number }>[] = [
      {
        title: t('masterdata.waste-grouping-environment-permit.stt'),
        dataIndex: 'stt',
        width: 40,
        align: 'center',
        fixed: 'left',
      },
      {
        title: t('masterdata.waste-grouping-contract.group-name'),
        dataIndex: 'wasteGroup',
        width: 150,
        fixed: 'left',
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.code'),
        dataIndex: 'wasteGroupingEnvironmentPermitCustomCode',
        width: 120,
        render: (_, record) => (
          <Link
            className="text-primary font-semibold hover:opacity-90"
            to={`/MasterData/WasteGroupingEnvironmentPermit/${record.wasteGroupingEnvironmentPermitId}`}
          >
            {record.wasteGroupingEnvironmentPermitCustomCode}
          </Link>
        ),
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.name'),
        dataIndex: 'wasteGroupingEnvironmentPermitName',
        width: 240,
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.volume'),
        dataIndex: 'quantity',
        width: 120,
        align: 'right',
        render: (_, record) => {
          if (record.quantity == null || record.quantity === undefined) return '-';
          return formatDecimalNumber(`${record.quantity}`);
        },
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.unit'),
        dataIndex: 'unitName',
        width: 80,
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.creator'),
        dataIndex: 'userName',
        width: 150,
      },
      // Bỏ hiện không dùng đến
      // {
      //   title: t('masterdata.waste-grouping-environment-permit.created-time'),
      //   dataIndex: 'createTime',
      //   width: 130,
      //   align: 'center',
      //   valueType: 'dateTime',
      //   fieldProps: {
      //     format: DATE_HOUR_MINUTE_FORMAT_DEFAULT,
      //   },
      // },
      {
        title: t('masterdata.waste-grouping-environment-permit.status'),
        dataIndex: 'actived',
        width: 70,
        align: 'center',
        render: (_, record) => (
          <div className="flex items-center justify-center">
            {record.actived ? (
              <Icon.CheckStatus className="!fill-[#389E0D]" width="15" />
            ) : (
              <Icon.CloseStatus className="!fill-[#D93133E5]" width="15" />
            )}
          </div>
        ),
      },
      {
        title: t('masterdata.waste-grouping-environment-permit.actions'),
        dataIndex: 'function',
        width: 80,
        align: 'center',
        fixed: 'right',
        render: (_, record) => (
          <div className="flex items-center justify-center gap-1">
            {userStore.userPermission?.includes('EDIT') ? (
              <Tooltip title={t('btn.edit')}>
                <CustomButton
                  className="!w-6 !h-6 rounded-sm !border-[#4DA8FF] flex items-center justify-center !bg-[#C9E8FF] hover:bg-[#95D2FF]"
                  icon={<Icon.PencilEdit className="!fill-[#007BF1]" width="16" height="16" />}
                  onClick={() => navigate(`${record.wasteGroupingEnvironmentPermitId}`)}
                />
              </Tooltip>
            ) : null}
            {userStore.userPermission?.includes('DELETE') ? (
              <Tooltip title={t('btn.delete')}>
                <CustomButton
                  className="!w-6 !h-6 rounded-sm !border-[#FF4F51] flex items-center justify-center !bg-[#FFE5E6] hover:bg-[#FF8081]"
                  icon={<Icon.Trash className="!fill-[#FC5C5E]" width="16" height="16" />}
                  onClick={() => showDeleteConfirm(record.wasteGroupingEnvironmentPermitName || '', record.wasteGroupingEnvironmentPermitId || '')}
                />
              </Tooltip>
            ) : null}
          </div>
        ),
      },
    ];

    const showDeleteConfirm = (name: string, id: string) => {
      confirm({
        title: t('masterdata.waste-grouping-environment-permit.delete-title'),
        icon: <ExclamationCircleFilled />,
        content: t('masterdata.waste-grouping-environment-permit.delete-content', { name }),
        okText: t('btn.delete'),
        okType: 'danger',
        cancelText: t('btn.cancel'),
        async onOk() {
          await wasteGroupingEnvironmentPermitPresenter.deleteWasteGroupingEnvironmentPermit(
            wasteGroupingEnvironmentPermitStore,
            id
          );
          if (wasteGroupingEnvironmentPermitStore.crudResponse.isSuccess) {
            message.success(wasteGroupingEnvironmentPermitStore.crudResponse.message);
            loadData(dataSearch);
          } else {
            message.error(wasteGroupingEnvironmentPermitStore.crudResponse.message);
          }
        },
      });
    };

    const handleExportTemplate = async () => {
      try {
        setLoading(true);
        const exportPayload = {
          wasteGroupingEnvironmentPermitCustomCode: dataSearch.wasteGroupingEnvironmentPermitCustomCode,
          wasteGroupingEnvironmentPermitName: dataSearch.wasteGroupingEnvironmentPermitName,
          wasteGroup: dataSearch.wasteGroup,
          actived: dataSearch.actived,
        };
        await wasteGroupingEnvironmentPermitPresenter.exportTemplateCreate(
          wasteGroupingEnvironmentPermitStore,
          exportPayload,
        );
        if (wasteGroupingEnvironmentPermitStore.exportResponse.isSuccess) {
          message.success(wasteGroupingEnvironmentPermitStore.exportResponse.message);
        } else {
          message.error(wasteGroupingEnvironmentPermitStore.exportResponse.message);
        }
      } catch (error: any) {
        message.error(error?.message || 'Có lỗi xảy ra khi export template');
      } finally {
        setLoading(false);
      }
    };

    const handleImport = async (file: File) => {
      try {
        setLoading(true);
        await wasteGroupingEnvironmentPermitPresenter.importWasteGroupingEnvironmentPermit(
          wasteGroupingEnvironmentPermitStore,
          file
        );
        if (wasteGroupingEnvironmentPermitStore.importResponse.isSuccess) {
          message.success(wasteGroupingEnvironmentPermitStore.importResponse.message);
          loadData(dataSearch);
        } else {
          message.error(wasteGroupingEnvironmentPermitStore.importResponse.message);
        }
      } catch (error: any) {
        message.error(error?.message || 'Có lỗi xảy ra khi import');
      } finally {
        setLoading(false);
      }
    };

    const handleImportCreate = async (file: File) => {
      try {
        setImportLoading(true);
        await wasteGroupingEnvironmentPermitPresenter.importWasteGroupingEnvironmentPermitCreate(
          wasteGroupingEnvironmentPermitStore,
          file
        );
        if (wasteGroupingEnvironmentPermitStore.importResponse.isSuccess) {
          message.success(wasteGroupingEnvironmentPermitStore.importResponse.message);
          loadData(dataSearch);
        } else {
          message.error(wasteGroupingEnvironmentPermitStore.importResponse.message);
        }
      } catch (error: any) {
        message.error(error?.message || 'Có lỗi xảy ra khi import');
      } finally {
        setImportLoading(false);
      }
    };

    const handleExportTemplateAdd = async () => {
      try {
        setLoading(true);
        await wasteGroupingEnvironmentPermitPresenter.exportTemplateAdd(
          wasteGroupingEnvironmentPermitStore
        );
        if (wasteGroupingEnvironmentPermitStore.exportResponse.isSuccess) {
          message.success(wasteGroupingEnvironmentPermitStore.exportResponse.message);
        } else {
          message.error(wasteGroupingEnvironmentPermitStore.exportResponse.message);
        }
      } catch (error: any) {
        message.error(error?.message || 'Có lỗi xảy ra khi export template');
      } finally {
        setLoading(false);
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleImport(file);
        // Reset input để có thể chọn lại cùng file
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    const fileInputCreateRef = useRef<HTMLInputElement>(null);
    const handleFileCreateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleImportCreate(file);
        // Reset input để có thể chọn lại cùng file
        if (fileInputCreateRef.current) {
          fileInputCreateRef.current.value = '';
        }
      }
    };

    return (
      <div className="w-full">
        <CustomBreadcrumb
          pagetitle={[{ title: t('masterdata.waste-grouping-environment-permit.title'), link: '/MasterData/WasteGroupingEnvironmentPermit' }]}
          extra={
            <div className="flex gap-2">
              <CustomButton type="primary" color="#EFF4F9" className="!px-3" onClick={() => setOpenDrawer(true)}>
                <Icon.Filter className="fill-primary" />
              </CustomButton>
              {isSysAdmin && (
                <>
                  <CustomButton
                    icon={<Icon.Upload />}
                    className="!bg-[#F3F4F6] text-primary"
                    onClick={() => fileInputCreateRef.current?.click()}
                    loading={importLoading}
                    disabled={importLoading}
                  >
                    Import Create
                  </CustomButton>
                  <input
                    ref={fileInputCreateRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileCreateChange}
                  />
                  <CustomButton
                    icon={<Icon.Download className="!fill-white" />}
                    type="primary"
                    color="#22c55e"
                    onClick={handleExportTemplateAdd}
                    loading={loading}
                  >
                    Export Create
                  </CustomButton>
                </>
              )}
              <CustomButton
                icon={<Icon.Upload />}
                className="!bg-[#F3F4F6] text-primary"
                onClick={() => fileInputRef.current?.click()}
                loading={loading}
              >
                Import
              </CustomButton>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <CustomButton
                icon={<Icon.Download className="!fill-white" />}
                type="primary"
                color="#22c55e"
                onClick={handleExportTemplate}
                loading={loading}
              >
                Export template
              </CustomButton>
              <CustomButton
                type="primary"
                icon={<Icon.Plus className="!fill-white" />}
                onClick={() => navigate('Create')}
              >
                {t('btn.add')}
              </CustomButton>
            </div>
          }
        />
        <BoxContainer>
          <CustomProTable<any>
            columns={columns}
            dataSource={displayData}
            rowKey="wasteGroupingEnvironmentPermitId"
            bordered
            search={false}
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              ...tablePagination(dataSearch as any, setDataSearch as any),
              total: wasteGroupingEnvironmentPermitStore.searchResponse.paging?.recordsTotal,
            }}
            options={false}
          />
        </BoxContainer>
        <SearchBox
          {...({ className: "lg:!w-[35%]" } as any)}
          footer={[
            <div key="footer" className="flex justify-end">
              <CustomButton
                type="primary"
                icon={<HiSearch />}
                htmlType="submit"
                onClick={() => {
                  (form as any).submit();
                }}
              >
                {t('btn.search')}
              </CustomButton>
            </div>,
          ]}
          onClose={() => setOpenDrawer(false)}
          open={openDrawer}
        >
          <SearchForm
            column="col-1"
            form={form}
            onFinish={handleSearch}
            initialValues={{ actived: true }}
          >
            {/* Nhóm chất thải từ catalog WasteType */}
            <SearchItem label={t('masterdata.waste-grouping-environment-permit.group')} name="group">
              <CustomSelect
                options={wasteGroupOptions}
                placeholder={t('masterdata.waste-grouping-environment-permit.group-placeholder')}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={customFilterOption}
              />
            </SearchItem>
            <SearchItem label={t('masterdata.waste-grouping-environment-permit.code')} name="code">
              <CustomInput placeholder={t('masterdata.waste-grouping-environment-permit.code-placeholder')} />
            </SearchItem>
            <SearchItem label={t('masterdata.waste-grouping-environment-permit.name')} name="name">
              <CustomInput placeholder={t('masterdata.waste-grouping-environment-permit.name-placeholder')} />
            </SearchItem>
            <SearchItem label={t('masterdata.waste-grouping-environment-permit.status')} name="actived">
              <CustomSelect options={dropdownStatus} placeholder={t('place-holder.all')} allowClear />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </div>
    );
  });
};

export default createWasteGroupingEnvironmentPermitPage;

